import { Injectable } from '@angular/core';

import {
  DprWeatherDetail,
  DprConsumableDetail,
  DprPersonnelDetail,
  DprHidden,
  DprProgressDetail,
  DprAttachment,
  DprHseDetail,
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
  DprProgressWithoutTargetDetail,
  DprFreeTextFieldDetail,
} from '@shared/models/dpr';
import {
  DprActivityCustomDataGridData,
  DprActivityGridData,
  DprForecastActivityGridData,
  DprSubActivityGridData,
} from '@dpr-details/models/grid';
import { SyncService } from '@shared/services/grid';
import { numericIdOrZero } from '@shared/utils';
import { DprAttachmentsGridData, Sync } from '@shared/models/grid';
import { DprSummaryGridSync } from '@dpr-details/models/sync/dpr-summary-grid-sync.model';
import {
  DprActivitiesGridSync,
  DprAppendixGridSync,
  DprConsumableGridSync,
  DprForecastActivitiesGridSync,
  DprFreeTextFieldSync,
  DprHiddenGridSync,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprProgressGridSync,
  DprProgressWithoutTargetGridSync,
  DprStatusTrackerGridSync,
  DprWeatherGridSync,
  WriteDprActivity,
  WriteDprActivityCustomData,
  WriteDprForecastActivity,
  WriteDprForecastActivityCustomData,
  WriteDprForecastSubActivity,
  WriteDprParallelActivity,
  WriteDprParallelActivityCustomData,
  WriteDprParallelSubActivity,
  WriteDprSubActivity,
} from '@dpr-details/models/sync';
import { DprTableOfColumnGridSync } from '@dpr-details/models/sync/dpr-table-of-column-grid-sync.model';

@Injectable()
export class DprDetailsSyncSanitizeService {
  constructor(private syncService: SyncService) {}

  //#region Sanitize methods

  //#region Activities

  sanitizeActivity(data: DprActivityGridData): WriteDprActivity {
    return {
      id: data.id as number,
      dprDetailId: data.dprDetailId,
      activityGroupId: data.activityGroupId,
      activityId: data.activityId,
      locationId: data.locationId,
      start: data.start,
      finish: data.finish,
      comment: data.comment,
      syncDprSubActivities: this.syncService.sanitizeModel(
        data.syncDprSubActivities,
        this.sanitizeSubActivity.bind(this)
      ),
      syncDprActivityCustomDataList: this.syncService.sanitizeModel(
        data.syncDprActivityCustomDataList,
        this.sanitizeCustomData.bind(this)
      ),
      syncDprActivityAttachments: this.syncService.sanitizeModel(
        data.syncDprActivityAttachments,
        this.sanitizeAttachment.bind(this)
      ),
    };
  }

  sanitizeParallelActivity(
    data: DprActivityGridData
  ): WriteDprParallelActivity {
    return {
      id: data.id as number,
      dprDetailId: data.dprDetailId,
      activityGroupId: data.activityGroupId,
      activityId: data.activityId,
      locationId: data.locationId,
      start: data.start,
      finish: data.finish,
      comment: data.comment,
      parallelActivityType: data.parallelActivityType,
      syncDprParallelSubActivities: this.syncService.sanitizeModel(
        data.syncDprSubActivities,
        this.sanitizeParallelSubActivity.bind(this)
      ),
      syncDprParallelActivityCustomDataList: this.syncService.sanitizeModel(
        data.syncDprActivityCustomDataList,
        this.sanitizeParallelCustomData.bind(this)
      ),
      syncDprParallelActivityAttachments: this.syncService.sanitizeModel(
        data.syncDprActivityAttachments,
        this.sanitizeAttachment.bind(this)
      ),
    };
  }

  sanitizeSubActivity(data: DprSubActivityGridData): WriteDprSubActivity {
    return {
      id: data.id,
      name: data.name,
      start: data.start,
      finish: data.finish,
      dprActivityId: data.dprActivityId,
    };
  }

  sanitizeParallelSubActivity(
    data: DprSubActivityGridData
  ): WriteDprParallelSubActivity {
    return {
      id: data.id,
      name: data.name,
      start: data.start,
      finish: data.finish,
      dprParallelActivityId: data.dprActivityId,
    };
  }

  sanitizeCustomData(
    data: DprActivityCustomDataGridData
  ): WriteDprActivityCustomData {
    return {
      id: data.id,
      customDataId: data.customDataId,
      value: data.value ? data.value.toString() : data.value,
      comment: data.comment,
      dprActivityId: data.dprActivityId,
    };
  }

  sanitizeAttachment(data: DprAttachmentsGridData): DprAttachment {
    return {
      id: data.id,
      attachmentId: data.attachmentId,
      attachment: {
        id: data.attachmentId,
        name: this.addExtension(data.name, data.extension),
        type: data.type,
        size: data.size,
        uploadDate: data.uploadDate,
        externalUrl: data.externalUrl,
        displayName: this.addExtension(data.displayName, data.extension),
        comment: data.comment,
        isImage: data.isImage,
        isPrintInReport: data.isPrintInReport,
        dprAttachmentType: data.dprAttachmentType,
        dprAttachmentOrder: data.dprAttachmentOrder,
        copyOf: data.copyOf,
        dprDetailId: data.dprDetailId,
      },
    };
  }

  sanitizeParallelCustomData(
    data: DprActivityCustomDataGridData
  ): WriteDprParallelActivityCustomData {
    return {
      id: data.id,
      customDataId: data.customDataId,
      value: data.value ? data.value.toString() : data.value,
      comment: data.comment,
      dprParallelActivityId: data.dprActivityId,
    };
  }

  //#endregion

  sanitizeStatusTrackerDetailItem(
    data: DprStatusTrackerDetails
  ): DprStatusTrackerDetails {
    return {
      id: data.id,
      dprStatusTrackerId: data.dprStatusTrackerId,
      rowId: data.rowId,
      columnId: data.columnId,
      colorTypeId: data.colorTypeId,
      value: data.value,
      statusTrackerId: data.statusTrackerId,
    };
  }

  //#region Weather

  sanitizeWeatherDetailItem(data: DprWeatherDetail): DprWeatherDetail {
    return {
      id: data.id,
      dprWeatherId: data.dprWeatherId,
      time: data.time,
      dprWeatherDetailsItems: data.dprWeatherDetailsItems,
    };
  }

  //#endregion

  //#region HSE

  sanitizeHseDetailItem(data: DprHseDetail): DprHseDetail {
    return {
      id: data.id as number,
      dprhseId: data.dprhseId,
      hseId: data.hseId,
      previous: data.previous,
      today: data.today,
      comment: data.comment,
    };
  }

  //#endregion

  //#region Personnel

  sanitizePersonnelDetailItem(data: DprPersonnelDetail): DprPersonnelDetail {
    return {
      id: data.id,
      dprPersonnelId: data.dprPersonnelId,
      personnelId: data.personnelId,
      previous: data.previous,
      today: data.today,
      used: data.used,
      added: data.added,
      currentDay: data.currentDay,
      workedHours: data.workedHours,
      comment: data.comment,
      dailyStockDetails: data.dailyStockDetails,
      workedHoursDetails: data.workedHoursDetails,
    };
  }

  //#endregion

  //#region Consumable

  sanitizeConsumableDetailItem(data: DprConsumableDetail): DprConsumableDetail {
    return {
      id: data.id,
      dprConsumableId: data.dprConsumableId,
      consumableId: data.consumableId,
      previous: data.previous,
      today: data.today,
      used: data.used,
      added: data.added,
      comment: data.comment,
      dailyStockDetails: data.dailyStockDetails,
    };
  }

  //#endregion

  //#region Forecast

  sanitizeForecastActivity(
    data: DprForecastActivityGridData
  ): WriteDprForecastActivity {
    return {
      id: data.id as number,
      activityGroupId: data.activityGroupId,
      activityId: data.activityId,
      locationId: data.locationId,
      forecastCategoryId: data.forecastCategoryId,
      date: new Date(data.date) ?? null,
      start: data.start,
      finish: data.finish,
      comment: data.comment,
      dprDetailId: data.dprDetailId,
      syncDprForecastSubActivities:
        this.syncService.sanitizeModel(
          data.syncDprForecastSubActivities,
          this.sanitizeForecastSubActivity.bind(this)
        ) ?? this.syncService.getDefaultSyncModel(),
      syncDprForecastActivityCustomDataList:
        this.syncService.sanitizeModel(
          data.syncDprForecastActivityCustomDataList,
          this.sanitizeForecastCustomData.bind(this)
        ) ?? this.syncService.getDefaultSyncModel(),
    };
  }

  sanitizeForecastSubActivity(
    data: WriteDprForecastSubActivity
  ): WriteDprForecastSubActivity {
    return {
      id: data.id,
      name: data.name,
      start: data.start,
      finish: data.finish,
      dprForecastActivityId: data.dprForecastActivityId,
    };
  }

  sanitizeForecastCustomData(
    data: WriteDprForecastActivityCustomData
  ): WriteDprForecastActivityCustomData {
    return {
      id: data.id,
      customDataId: data.customDataId,
      value: data.value,
      comment: data.comment,
      dprForecastActivityId: data.dprForecastActivityId,
    };
  }

  //#endregion

  //#region Free Text Field

  sanitizeFreeTextField(data: DprFreeTextFieldDetail): DprFreeTextFieldDetail {
    return {
      id: data.id,
      comment: data.comment,
      dprFreeTextFieldId: numericIdOrZero(data.dprFreeTextFieldId),
      freeTextFieldItemId: data.freeTextFieldItemId,
    };
  }

  //#endregion

  //#region Progress

  sanitizeProgressDetails(data: DprProgressDetail): DprProgressDetail {
    return {
      id: data.id,
      dprProgressId: data.dprProgressId,
      progressId: data.progressId,
      clearedToday: data.clearedToday,
      comment: data.comment,
      progressStockDetails: data.progressStockDetails,
    };
  }

  //#endregion

  //#region Progress without Target

  sanitizeProgressWithoutTargetDetails(
    data: DprProgressWithoutTargetDetail
  ): DprProgressWithoutTargetDetail {
    return {
      id: data.id,
      dprProgressWithoutTargetId: data.dprProgressWithoutTargetId,
      progressWithoutTargetId: data.progressWithoutTargetId,
      clearedToday: data.clearedToday,
      comment: data.comment,
      clearedTotal: data.clearedTotal,
    };
  }

  //#endregion

  //#region Table Of Column

  sanitizeTableOfColumnDetailItem(
    data: DprTableOfColumnDetail
  ): DprTableOfColumnDetail {
    return {
      id: data.id,
      dprTableOfColumnId: numericIdOrZero(data.dprTableOfColumnId),
      dprTableOfColumnDetailItems: data.dprTableOfColumnDetailItems,
    };
  }

  //#endregion

  //#region Hidden

  sanitizeHidden(data: DprHidden): DprHidden {
    return {
      id: data.id,
      comment: data.comment,
      dprDetailId: data.dprDetailId,
      hiddenSubtabId: data.hiddenSubtabId,
      syncDprHiddenAttachments: data.syncDprHiddenAttachments,
    };
  }

  //#endregion

  //#endregion

  //#region Clear added record ID methods

  clearActivityAddedRecordIds(
    data: DprActivitiesGridSync
  ): DprActivitiesGridSync {
    return {
      ...data,
      syncDPRActivities: this.clearSyncDprActivityAddedRecordIds(
        data.syncDPRActivities
      ),
      syncDPRParallelActivities:
        this.clearSyncDprParallelActivityAddedRecordIds(
          data.syncDPRParallelActivities
        ),
    };
  }

  clearStatusTrackerAddedRecordIds(
    data: DprStatusTrackerGridSync
  ): DprStatusTrackerGridSync {
    return {
      ...data,
      dprStatusTracker: {
        ...data.dprStatusTracker,
        id: numericIdOrZero(data.dprStatusTracker.id),
        syncDprStatusTrackerDetails: this.clearAddedRecordIds(
          data.dprStatusTracker.syncDprStatusTrackerDetails
        ),
      },
    };
  }

  clearWeatherAddedRecordIds(data: DprWeatherGridSync): DprWeatherGridSync {
    return {
      ...data,
      dprWeather: {
        ...data.dprWeather,
        syncDprWeatherDetails: this.clearAddedRecordIds(
          data.dprWeather.syncDprWeatherDetails
        ),
        syncDprWeatherAttachments: this.clearAddedRecordIds(
          data.dprWeather.syncDprWeatherAttachments
        ),
      },
    };
  }

  clearPersonnelAddedRecordIds(
    data: DprPersonnelGridSync
  ): DprPersonnelGridSync {
    return {
      ...data,
      dprPersonnel: {
        ...data.dprPersonnel,
        syncDprPersonnelDetails: this.clearAddedRecordIds(
          data.dprPersonnel.syncDprPersonnelDetails
        ),
        syncDprPersonnelAttachments: this.clearAddedRecordIds(
          data.dprPersonnel.syncDprPersonnelAttachments
        ),
      },
    };
  }

  clearConsumableAddedRecordIds(
    data: DprConsumableGridSync
  ): DprConsumableGridSync {
    return {
      ...data,
      dprConsumable: {
        ...data.dprConsumable,
        syncDprConsumableDetails: this.clearAddedRecordIds(
          data.dprConsumable.syncDprConsumableDetails
        ),
        syncDprConsumableAttachments: this.clearAddedRecordIds(
          data.dprConsumable.syncDprConsumableAttachments
        ),
      },
    };
  }

  clearHseAddedRecordIds(data: DprHseGridSync): DprHseGridSync {
    return {
      ...data,
      dprHse: {
        ...data.dprHse,
        syncDprHseDetails: this.clearAddedRecordIds(
          data.dprHse.syncDprHseDetails
        ),
        syncDprHseAttachments: this.clearAddedRecordIds(
          data.dprHse.syncDprHseAttachments
        ),
      },
    };
  }

  clearSummaryAddedRecordIds(data: DprSummaryGridSync): DprSummaryGridSync {
    return {
      ...data,
      dprSummary: {
        ...data.dprSummary,
        syncDprSummaryAttachments: this.clearAddedRecordIds(
          data.dprSummary.syncDprSummaryAttachments
        ),
      },
    };
  }

  clearTableOfColumnsAddedRecordIds(
    data: DprTableOfColumnGridSync
  ): DprTableOfColumnGridSync {
    const table = data.dprTableOfColumn;

    return {
      ...data,
      dprTableOfColumn: {
        ...table,
        id: numericIdOrZero(table.id),
        syncDprTableOfColumnDetails: this.clearAddedRecordIds(
          table.syncDprTableOfColumnDetails
        ),
        syncDprTableOfColumnAttachments: this.clearAddedRecordIds(
          table.syncDprTableOfColumnAttachments
        ),
      },
    };
  }

  clearForecastActivitiesAddedRecordIds(
    data: DprForecastActivitiesGridSync
  ): DprForecastActivitiesGridSync {
    return {
      ...data,
      syncDprForecastActivities:
        this.clearSyncDprForecastActivityAddedRecordIds(
          data.syncDprForecastActivities
        ),
    };
  }

  clearAppendixAddedRecordIds(data: DprAppendixGridSync): DprAppendixGridSync {
    return {
      ...data,
      syncDprAppendixAttachments: this.clearAddedRecordIds(
        data.syncDprAppendixAttachments
      ),
    };
  }

  clearHiddenAddedRecordIds(data: DprHiddenGridSync): DprHiddenGridSync {
    // `syncDprHidden` is a list of subtabs, each owning a nested attachments
    // sync. Clear the attachment ids on every subtab, then clear the added
    // subtab ids via the outer clearAddedRecordIds.
    const clearAttachments = (subtab: DprHidden): DprHidden => ({
      ...subtab,
      syncDprHiddenAttachments: this.clearAddedRecordIds(
        subtab.syncDprHiddenAttachments
      ),
    });

    return {
      ...data,
      syncDprHidden: this.clearAddedRecordIds({
        addedList: data.syncDprHidden?.addedList?.map(clearAttachments),
        updatedList: data.syncDprHidden?.updatedList?.map(clearAttachments),
        deletedIds: data.syncDprHidden?.deletedIds,
      }),
    };
  }

  clearProgressWithoutTargetAddedRecordIds(
    data: DprProgressWithoutTargetGridSync
  ): DprProgressWithoutTargetGridSync {
    return {
      ...data,
      dprProgressWithoutTarget: {
        ...data.dprProgressWithoutTarget,
        syncDprProgressWithoutTargetDetails: this.clearAddedRecordIds(
          data.dprProgressWithoutTarget.syncDprProgressWithoutTargetDetails
        ),
        syncDprProgressWithoutTargetAttachments: this.clearAddedRecordIds(
          data.dprProgressWithoutTarget.syncDprProgressWithoutTargetAttachments
        ),
      },
    };
  }

  clearProgressAddedRecordIds(data: DprProgressGridSync): DprProgressGridSync {
    return {
      ...data,
      dprprogress: {
        ...data.dprprogress,
        syncDprProgressDetails: this.clearAddedRecordIds(
          data.dprprogress.syncDprProgressDetails
        ),
        syncDprProgressAttachments: this.clearAddedRecordIds(
          data.dprprogress.syncDprProgressAttachments
        ),
      },
    };
  }

  clearFreeTextFieldAddedRecordIds(
    data: DprFreeTextFieldSync
  ): DprFreeTextFieldSync {
    return {
      ...data,
      dprFreeTextField: {
        ...data.dprFreeTextField,
        id: numericIdOrZero(data.dprFreeTextField.id),
        syncDprFreeTextFieldDetails: this.clearAddedRecordIds(
          data.dprFreeTextField.syncDprFreeTextFieldDetails
        ),
      },
    };
  }

  //#endregion

  //#region Private methods

  private clearAddedRecordIds(data: Sync<any>): Sync<any> {
    if (!data) {
      return null;
    }

    return {
      addedList: data.addedList?.map((x) => ({
        ...x,
        id: null,
      })),
      updatedList: data.updatedList,
      deletedIds: data.deletedIds,
    };
  }

  private clearSyncDprActivityAddedRecordIds(
    data: Sync<WriteDprActivity>
  ): Sync<WriteDprActivity> {
    if (!data) {
      return this.syncService.getDefaultSyncModel();
    }

    return {
      addedList: data.addedList?.map((x) => ({
        ...x,
        id: null,
        syncDprSubActivities: this.clearAddedRecordIds(x.syncDprSubActivities),
        syncDprActivityCustomDataList: this.clearAddedRecordIds(
          x.syncDprActivityCustomDataList
        ),
        syncDprActivityAttachments: this.clearAddedRecordIds(
          x.syncDprActivityAttachments
        ),
      })),
      updatedList: data.updatedList?.map((x) => ({
        ...x,
        syncDprSubActivities: this.clearAddedRecordIds(x.syncDprSubActivities),
        syncDprActivityCustomDataList: this.clearAddedRecordIds(
          x.syncDprActivityCustomDataList
        ),
        syncDprActivityAttachments: this.clearAddedRecordIds(
          x.syncDprActivityAttachments
        ),
      })),
      deletedIds: data.deletedIds,
    };
  }

  private clearSyncDprParallelActivityAddedRecordIds(
    data: Sync<WriteDprParallelActivity>
  ): Sync<WriteDprParallelActivity> {
    if (!data) {
      return this.syncService.getDefaultSyncModel();
    }

    return {
      addedList: data.addedList?.map((x) => ({
        ...x,
        id: null,
        syncDprParallelSubActivities: this.clearAddedRecordIds(
          x.syncDprParallelSubActivities
        ),
        syncDprParallelActivityCustomDataList: this.clearAddedRecordIds(
          x.syncDprParallelActivityCustomDataList
        ),
        syncDprParallelActivityAttachments: this.clearAddedRecordIds(
          x.syncDprParallelActivityAttachments
        ),
      })),
      updatedList: data.updatedList?.map((x) => ({
        ...x,
        syncDprParallelSubActivities: this.clearAddedRecordIds(
          x.syncDprParallelSubActivities
        ),
        syncDprParallelActivityCustomDataList: this.clearAddedRecordIds(
          x.syncDprParallelActivityCustomDataList
        ),
        syncDprParallelActivityAttachments: this.clearAddedRecordIds(
          x.syncDprParallelActivityAttachments
        ),
      })),
      deletedIds: data.deletedIds,
    };
  }

  private clearSyncDprForecastActivityAddedRecordIds(
    data: Sync<WriteDprForecastActivity>
  ): Sync<WriteDprForecastActivity> {
    if (!data) {
      return this.syncService.getDefaultSyncModel();
    }

    return {
      addedList: data.addedList?.map((x) => ({
        ...x,
        id: null,
        syncDprForecastSubActivities: this.clearAddedRecordIds(
          x.syncDprForecastSubActivities
        ),
        syncDprForecastActivityCustomDataList: this.clearAddedRecordIds(
          x.syncDprForecastActivityCustomDataList
        ),
      })),
      updatedList: data.updatedList?.map((x) => ({
        ...x,
        syncDprForecastSubActivities: this.clearAddedRecordIds(
          x.syncDprForecastSubActivities
        ),
        syncDprForecastActivityCustomDataList: this.clearAddedRecordIds(
          x.syncDprForecastActivityCustomDataList
        ),
      })),
      deletedIds: data.deletedIds,
    };
  }

  private addExtension(name: string, extension: string): string {
    return extension ? `${name}.${extension}` : name;
  }

  //#endregion
}
